import {ReactNode} from 'react';
import {styled} from 'styled-components';

import {removeUndefined} from '@shared/lib/utils/type_utils';
import {Door} from '@shared/models/api_models';
import {SupportedLanguage} from '@shared/models/language';

import {Flex} from '@shared_web/components/core2/flex';

import {translate} from '@mavismart_frontend/components/translate';

export const DOOR_SEARCH_FIELDS = [
  {key: 'id', weight: 4},
  {key: 'name1', weight: 6},
  {key: 'name2', weight: 5},
  {key: 'name3', weight: 4},
  {key: 'customerName', weight: 5},
  {key: 'label', weight: 5},
  {key: 'customAddress', weight: 4},
  {key: 'address1', weight: 4},
  {key: 'address2', weight: 3},
  {key: 'address3', weight: 3},
  {key: 'postalCode', weight: 4},
  {key: 'city', weight: 4},
  {key: 'country', weight: 3},
  {key: 'curtainColor', weight: 2},
  {key: 'structureColor', weight: 2},
  {key: 'classification', weight: 2},
  {key: 'voltage', weight: 2},
  {key: 'cabinet', weight: 2},
] satisfies {key: keyof Door; weight: number}[];
export type DoorSearchField = (typeof DOOR_SEARCH_FIELDS)[number]['key'];

function normalizeStringForSearch(str: string): string {
  return str.toLowerCase();
}

export function applySearchQueryOnDoor(opts: {searchQuery: string; door: Door}): {
  matches: {field: DoorSearchField; index: number; score: number}[];
  maxScore: number;
  sumScore: number;
} {
  const {searchQuery, door} = opts;
  const searchStr = normalizeStringForSearch(searchQuery);

  const matches: {field: DoorSearchField; index: number; score: number}[] = [];
  let maxScore = 0;
  let sumScore = 0;

  if (searchStr === '') {
    return {matches, maxScore, sumScore};
  }

  for (const field of DOOR_SEARCH_FIELDS) {
    const rawValue = door[field.key];
    if (!rawValue) {
      continue;
    }

    const valueStr = normalizeStringForSearch(String(rawValue));
    const index = valueStr.indexOf(searchStr);

    if (index === -1) {
      continue;
    }

    const score = field.weight * (index === 0 ? 2 : 1);

    matches.push({field: field.key, index, score});
    sumScore += score;
    if (score > maxScore) {
      maxScore = score;
    }
  }

  return {matches, maxScore, sumScore};
}

export function formatSearchMatchesSubtitle(opts: {
  door: Door;
  matches: {field: DoorSearchField; index: number; score: number}[];
  searchQuery: string;
  lang: SupportedLanguage;
}): ReactNode {
  const {door, matches, searchQuery, lang} = opts;

  if (matches.length === 0) {
    return door.setupDate ?? '...';
  }

  // Sort matches by score (descending) to show most relevant first
  const sortedMatches = [...matches].sort((a, b) => b.score - a.score);
  const searchStr = normalizeStringForSearch(searchQuery);

  // Show up to 3 matching fields
  const [bestMatch] = sortedMatches;
  const displayedMatches = removeUndefined([bestMatch]);

  return (
    <Flex $column>
      {displayedMatches.map((match, idx) => {
        const fieldValue = door[match.field];
        if (!fieldValue) {
          return null;
        }

        const valueStr = String(fieldValue);
        const normalizedValue = normalizeStringForSearch(valueStr);
        const normalizedMatchIndex = normalizedValue.indexOf(searchStr);

        if (normalizedMatchIndex === -1) {
          return null;
        }

        // Find the match in the original string (case-insensitive)
        // We need to account for the fact that normalization might change character positions
        const searchLower = searchQuery.toLowerCase();
        const valueLower = valueStr.toLowerCase();
        let matchIndex = valueLower.indexOf(searchLower);

        // If direct search doesn't work, try to find it by character position approximation
        if (matchIndex === -1) {
          // Fallback: try to find by searching for the normalized pattern in original
          matchIndex = 0; // Default to start if we can't find it
          for (let i = 0; i <= valueStr.length - searchQuery.length; i++) {
            const substr = valueStr.slice(i, i + searchQuery.length);
            if (normalizeStringForSearch(substr) === searchStr) {
              matchIndex = i;
              break;
            }
          }
        }

        const beforeMatch = valueStr.slice(0, matchIndex);
        const matchText = valueStr.slice(matchIndex, matchIndex + searchQuery.length);
        const afterMatch = valueStr.slice(matchIndex + searchQuery.length);

        // Map field names to translation keys, using existing translations where available
        const fieldTranslationMap: Record<string, string> = {
          label: 'label',
          cabinet: 'cabinet',
          curtainColor: 'curtain_color',
          structureColor: 'structure_color',
          classification: 'classification',
          voltage: 'voltage',
        };
        const baseTranslationKey = (fieldTranslationMap[match.field] ??
          `search_field__${match.field}`) as Parameters<typeof translate>[0];
        const translated = translate(baseTranslationKey, lang);
        // Remove trailing colon if present (existing translations have colons, but we add one in the JSX)
        const fieldLabel = translated.endsWith(' :') ? translated.slice(0, -2) : translated;

        return (
          <SearchMatchLine key={`${match.field}-${idx}`}>
            <strong>{fieldLabel}:&nbsp;</strong>
            <span>{beforeMatch}</span>
            <HighlightSpan>{matchText}</HighlightSpan>
            <span>{afterMatch}</span>
          </SearchMatchLine>
        );
      })}
    </Flex>
  );
}

const HighlightSpan = styled.span`
  background-color: #ffff0077;
  color: #000000dd;
  border-radius: 3px;
  padding: 1px 0;
  font-weight: 600;
`;

const SearchMatchLine = styled(Flex)`
  white-space: nowrap;
  align-items: baseline;
  text-overflow: ellipsis;
  overflow: hidden;
  width: calc(100% + 20px);
`;
